// src/features/task/taskLifecycle.ts
// Pure logic behind the task type lifecycle editors (statuses + transition graph).
//
// Two facts from the service shape everything here:
//
//  1. `TaskTransitionRule.from_status` / `.to_statuses` hold *base* TaskStatus
//     enum names — the service compares them against `task.Status.String()`
//     (services/task/internal/services/v1/transition_rules.go). Custom status
//     definitions are not graph nodes; they decorate a base status through
//     their `category`, which is also what boards and stats group by.
//
//  2. An empty `transitions` list is not "nothing allowed" — it means the type
//     falls back to the platform's default state machine, mirrored below in
//     DEFAULT_TRANSITION_MAP. A non-empty list is strict: only listed edges pass.

import type { TaskStatusDefinition, TaskTransitionRule } from '@core/types/task';

/** Every base status a transition rule may name, in lifecycle order. */
export const BASE_TASK_STATUSES = [
  'TASK_STATUS_DRAFT',
  'TASK_STATUS_OPEN',
  'TASK_STATUS_IN_PROGRESS',
  'TASK_STATUS_IN_REVIEW',
  'TASK_STATUS_BLOCKED',
  'TASK_STATUS_DONE',
  'TASK_STATUS_CANCELLED',
  'TASK_STATUS_ARCHIVED',
] as const;

const STATUS_LABEL_KEYS: Record<string, string> = {
  TASK_STATUS_DRAFT: 'task.status.draft',
  TASK_STATUS_OPEN: 'task.status.open',
  TASK_STATUS_IN_PROGRESS: 'task.status.inProgress',
  TASK_STATUS_IN_REVIEW: 'task.status.inReview',
  TASK_STATUS_BLOCKED: 'task.status.blocked',
  TASK_STATUS_DONE: 'task.status.done',
  TASK_STATUS_CANCELLED: 'task.status.cancelled',
  TASK_STATUS_ARCHIVED: 'task.status.archived',
};

/**
 * i18n key for a base status, or '' when the value is not one the platform
 * knows — the caller then shows the raw identifier rather than a wrong label.
 */
export function statusLabelKey(status: string | undefined): string {
  return (status && STATUS_LABEL_KEYS[status]) || '';
}

/**
 * The state machine a type runs on while its `transitions` list is empty.
 * Materialised verbatim by "start from the default state machine" so the first
 * strict version of a type behaves exactly like the free one did.
 */
export const DEFAULT_TRANSITION_MAP: Record<string, string[]> = {
  TASK_STATUS_DRAFT: ['TASK_STATUS_OPEN'],
  TASK_STATUS_OPEN: ['TASK_STATUS_IN_PROGRESS', 'TASK_STATUS_BLOCKED', 'TASK_STATUS_CANCELLED'],
  TASK_STATUS_IN_PROGRESS: ['TASK_STATUS_IN_REVIEW', 'TASK_STATUS_BLOCKED', 'TASK_STATUS_DONE', 'TASK_STATUS_CANCELLED'],
  TASK_STATUS_IN_REVIEW: ['TASK_STATUS_IN_PROGRESS', 'TASK_STATUS_BLOCKED', 'TASK_STATUS_DONE', 'TASK_STATUS_CANCELLED'],
  TASK_STATUS_BLOCKED: ['TASK_STATUS_OPEN', 'TASK_STATUS_IN_PROGRESS', 'TASK_STATUS_CANCELLED'],
  TASK_STATUS_DONE: ['TASK_STATUS_OPEN', 'TASK_STATUS_ARCHIVED'],
  TASK_STATUS_CANCELLED: ['TASK_STATUS_OPEN', 'TASK_STATUS_ARCHIVED'],
  TASK_STATUS_ARCHIVED: [],
};

/** A rule with no conditions attached — the shape "add rule" starts from. */
export function emptyTransitionRule(fromStatus: string): TaskTransitionRule {
  return {
    from_status: fromStatus,
    to_statuses: [],
    required_fields: [],
    required_roles: [],
    requires_comment: false,
  };
}

/**
 * The default map as editable rules. Terminal statuses get no rule at all —
 * a rule with no targets would read as "configured to go nowhere" when the
 * truth is simply that nothing leaves ARCHIVED.
 */
export function defaultTransitionRules(): TaskTransitionRule[] {
  return BASE_TASK_STATUSES.filter((status) => (DEFAULT_TRANSITION_MAP[status] || []).length > 0).map(
    (status) => ({
      ...emptyTransitionRule(status),
      to_statuses: [...DEFAULT_TRANSITION_MAP[status]],
    }),
  );
}

/** True when the rule set is byte-identical to the default state machine. */
export function isDefaultTransitionSet(rules: TaskTransitionRule[]): boolean {
  const defaults = defaultTransitionRules();
  if (rules.length !== defaults.length) return false;
  return defaults.every((expected, index) => {
    const actual = rules[index];
    return (
      actual &&
      actual.from_status === expected.from_status &&
      actual.to_statuses.join('|') === expected.to_statuses.join('|') &&
      !actual.requires_comment &&
      (actual.required_fields?.length ?? 0) === 0 &&
      (actual.required_roles?.length ?? 0) === 0 &&
      !actual.approval_pipeline?.id
    );
  });
}

export interface LifecycleAnalysis {
  /** Statuses the graph actually talks about — no noise about unused ones. */
  nodes: string[];
  /** Reachable from the type's initial status by following the rules. */
  reachable: string[];
  /** Named by a rule but not reachable from the initial status. */
  unreachable: string[];
  /** No rule leaves these — a task that lands there stays there. */
  terminal: string[];
  /** The statuses that count as "finished" for this type. */
  completionTargets: string[];
  /** Whether any completion target is reachable from the initial status. */
  hasCompletionPath: boolean;
  /** Indexes of rules that allow no target at all. */
  emptyRuleIndexes: number[];
  /** Source statuses covered by more than one rule (the first match wins). */
  duplicateSources: string[];
  /** The status a task of this type starts in. */
  initialStatus: string;
}

const FALLBACK_INITIAL_STATUS = 'TASK_STATUS_OPEN';
const FALLBACK_COMPLETION_STATUS = 'TASK_STATUS_DONE';

function isRealStatus(status: string | undefined): status is string {
  return !!status && status !== 'TASK_STATUS_UNSPECIFIED';
}

/**
 * Reads the rules as a directed graph and reports what an admin cannot see by
 * scanning a list: which statuses can never be entered, which are dead ends,
 * and whether work can finish at all.
 *
 * These are warnings, never errors. A half-built lifecycle is a normal state to
 * save in, and a type whose graph is deliberately open-ended is legitimate.
 */
export function analyzeTransitions(
  rules: TaskTransitionRule[],
  initialStatus: string | undefined,
  completionStatus: string | undefined,
): LifecycleAnalysis {
  const start = isRealStatus(initialStatus) ? initialStatus : FALLBACK_INITIAL_STATUS;
  const completionTargets = isRealStatus(completionStatus)
    ? [completionStatus]
    : [FALLBACK_COMPLETION_STATUS];

  const empty: LifecycleAnalysis = {
    nodes: [],
    reachable: [],
    unreachable: [],
    terminal: [],
    completionTargets,
    hasCompletionPath: true,
    emptyRuleIndexes: [],
    duplicateSources: [],
    initialStatus: start,
  };
  if (rules.length === 0) return empty;

  const adjacency = new Map<string, Set<string>>();
  const nodeSet = new Set<string>([start]);
  const sourceCounts = new Map<string, number>();
  const emptyRuleIndexes: number[] = [];

  rules.forEach((rule, index) => {
    const from = rule.from_status || '';
    const targets = (rule.to_statuses || []).filter(Boolean);
    if (from) {
      nodeSet.add(from);
      sourceCounts.set(from, (sourceCounts.get(from) ?? 0) + 1);
      const bucket = adjacency.get(from) ?? new Set<string>();
      targets.forEach((target) => {
        bucket.add(target);
        nodeSet.add(target);
      });
      adjacency.set(from, bucket);
    }
    if (targets.length === 0) emptyRuleIndexes.push(index);
  });

  const visited = new Set<string>([start]);
  const queue = [start];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const next of adjacency.get(current) ?? []) {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }

  // Report in lifecycle order, with anything unrecognised last, so the lists
  // read the same way every time rather than in rule-authoring order.
  const order = (status: string) => {
    const index = (BASE_TASK_STATUSES as readonly string[]).indexOf(status);
    return index < 0 ? BASE_TASK_STATUSES.length : index;
  };
  const nodes = Array.from(nodeSet).sort((a, b) => order(a) - order(b) || a.localeCompare(b));

  return {
    nodes,
    reachable: nodes.filter((node) => visited.has(node)),
    unreachable: nodes.filter((node) => !visited.has(node)),
    terminal: nodes.filter((node) => (adjacency.get(node)?.size ?? 0) === 0),
    completionTargets,
    hasCompletionPath: completionTargets.some((target) => visited.has(target)),
    emptyRuleIndexes,
    duplicateSources: Array.from(sourceCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([source]) => source)
      .sort((a, b) => order(a) - order(b)),
    initialStatus: start,
  };
}

/**
 * Task fields a transition may demand before it will run.
 *
 * These are the names `taskFieldIsSet` understands
 * (services/task/internal/services/v1/transition_rules.go) — a name outside
 * this set is treated as satisfied by the service, so offering anything else
 * would be offering a gate that silently does nothing.
 */
export const TRANSITION_TASK_FIELDS = [
  'title',
  'description',
  'assignee',
  'watchers',
  'priority',
  'due_date',
  'start_date',
  'estimated_duration',
  'tags',
  'parent',
  'progress_percent',
  'checklist_complete',
  'resources',
  'conversation',
];

/**
 * Roles a transition may be restricted to. Any one of them is enough.
 *
 * `assignee` and `creator` are properties of the task; the rest are relations
 * in the authorization model. A role outside this set is logged and ignored by
 * the service, so the picker is closed rather than free text.
 */
export const TRANSITION_ROLES = [
  'assignee',
  'creator',
  'owner',
  'admin',
  'manager',
  'editor',
  'member',
  'viewer',
  'project_admin',
  'project_member',
  'tenant_admin',
  'platform_admin',
];

/** Slug rules for a custom status, matching validateStatusDefinition. */
export function slugifyStatus(value: string): string {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return /^[a-z]/.test(slug) ? slug : slug ? `s_${slug}` : '';
}

/** Position is the board column order, so it is always a dense 0..n-1 run. */
export function withNormalizedPositions(defs: TaskStatusDefinition[]): TaskStatusDefinition[] {
  return defs.map((def, index) => ({ ...def, position: index }));
}

/** Move one definition up or down, renumbering positions. */
export function moveStatusDefinition(
  defs: TaskStatusDefinition[],
  index: number,
  delta: number,
): TaskStatusDefinition[] {
  const target = index + delta;
  if (target < 0 || target >= defs.length) return defs;
  const next = [...defs];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return withNormalizedPositions(next);
}

/** Definitions in board order. */
export function sortStatusDefinitions(defs: TaskStatusDefinition[]): TaskStatusDefinition[] {
  return [...defs].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/** The custom statuses that fall into a base status' column. */
export function statusesInCategory(
  defs: TaskStatusDefinition[],
  category: string,
): TaskStatusDefinition[] {
  return sortStatusDefinitions(defs.filter((def) => def.category === category));
}
